import React from "react"
import {
	Flex,
	Text,
	Image,
	Button,
	Rte,
	INNER_MAX_WIDTH,
	READABLE_LINE_MAX_WIDTH,
} from "@capioweb/ui-components"
import { QBankImage, XhtmlString } from "../../@types/CapioWeb/core"
import { StringProperty } from "../../@types/EpiFoundationSpa/Property"
import { BreadcrumbItemProps } from "../../components/Core/pageBase"
import { BreadcrumbsBlock } from "../../components"
import { createMarkup, imageBlockDataToImageProps } from "../../utils"
import { sendJobAdDataLayerPush } from "../../utils/piwikPro"

interface VarbiCareerPageProps {
	breadcrumb: Array<BreadcrumbItemProps>
	topImageLink: StringProperty
	heading: StringProperty
	description: XhtmlString
	applyByDate: string
	created: StringProperty
	startPublish: string
	employmentType: StringProperty
	employmentRatio: StringProperty
	employmentEndDate: string
	employmentWorkingHours: StringProperty
	salary: StringProperty
	employmentAdmission: StringProperty
	contact: XhtmlString
	unionRepresentatives: XhtmlString
	externalApplicationLink: StringProperty
	aboutUnit: XhtmlString
	aboutCapio: XhtmlString
	conditionsOfEmploymentTitle: string
	employmentTypeTitle: string
	employmentRatioTitle: string
	employmentWorkingHoursTitle: string
	salaryTitle: string
	employmentAdmissionTitle: string
	contactTitle: string
	applyButtonText: string
	aboutCapioTitle: string
	applicationLink?: StringProperty
	applicationLinkText?: string
	careerImage?: QBankImage
	aboutUnitTitle?: string
	descriptionTitle?: string
	modelType: string
}

const VarbiCareerPage: React.FC<VarbiCareerPageProps> = (props) => {
	const hasImage = props.topImageLink?.value ? true : false

	return (
		<Flex display="block" w={1}>
			<BreadcrumbsBlock items={...props.breadcrumb} />
			<Flex as="section" w={1} px={3}>
				<Flex
					w={1}
					flexDirection="column"
					maxw={INNER_MAX_WIDTH}
					style={{ margin: "0 auto" }}
				>
					<Flex
						w={{ _: "calc(100% + 48px)", m: 1 }}
						ml={{ _: -3, m: 0 }}
						mb={hasImage ? 4 : 5}
					>
						{hasImage && (
							<Image
								data-epi-edit="imageDesktopBlock"
								src={props.topImageLink.value}
								ratio={{ _: 420 / 721, l: 420 / 1128 }}
								cover
							/>
						)}
						{props.careerImage?.media?.value?.url && (
							<Image
								{...imageBlockDataToImageProps(props.careerImage, true)}
								ratio={{ _: 420 / 721, l: 420 / 1128 }}
							/>
						)}
					</Flex>
					<Flex
						w={1}
						flexDirection={{ _: "column", l: "row" }}
						columnGap={{ l: 3 }}
					>
						<Flex
							w={1}
							flex={{ _: 1, l: 8 / 12 }}
							flexDirection="column"
							mb={{ _: 2, m: 4 }}
							maxw={READABLE_LINE_MAX_WIDTH}
						>
							{props.heading?.value && (
								<Text variant="headline-1">{props.heading.value}</Text>
							)}

							<Flex flexDirection="column" mb={3}>
								{props.modelType === "VarbiCareerPage" && (
									<>
										{props.applyByDate && (
											<Text fontSize={2.5} lineHeight={4} mb={0}>
												{props.applyByDate}
											</Text>
										)}
										{props.startPublish && (
											<Text fontSize={2.5} lineHeight={4} mb={0}>
												{props.startPublish}
											</Text>
										)}
									</>
								)}
							</Flex>

							{props.aboutUnit?.value && props.aboutUnitTitle && (
								<Flex flexDirection="column" mb={3}>
									<Text variant="headline-2">{props.aboutUnitTitle}</Text>
									<Rte>
										<div
											dangerouslySetInnerHTML={createMarkup(
												props.aboutUnit.value
											)}
										/>
									</Rte>
								</Flex>
							)}
							{props.descriptionTitle && (
								<Text variant="headline-2">{props.descriptionTitle}</Text>
							)}

							{props.description?.value && (
								<Flex mb={3}>
									<Rte>
										<div
											dangerouslySetInnerHTML={createMarkup(
												props.description.value
											)}
										/>
									</Rte>
								</Flex>
							)}

							<Flex flexDirection="column" mb={3}>
								{props.conditionsOfEmploymentTitle && (
									<Text variant="headline-2" mb={3}>
										{props.conditionsOfEmploymentTitle}
									</Text>
								)}

								{props.employmentTypeTitle && props.employmentType?.value && (
									<Flex>
										<Text mb={0}>{props.employmentTypeTitle}:&nbsp;</Text>
										<Text mb={0}>{props.employmentType.value}</Text>
									</Flex>
								)}

								{props.employmentRatioTitle && props.employmentRatio?.value && (
									<Flex>
										<Text mb={0}>Omfattning:&nbsp;</Text>
										<Text mb={0}>{props.employmentRatio.value}</Text>
									</Flex>
								)}

								{props.employmentWorkingHoursTitle &&
									props.employmentWorkingHours?.value && (
										<Flex>
											<Text mb={0}>
												{props.employmentWorkingHoursTitle}:&nbsp;
											</Text>
											<Text mb={0}>{props.employmentWorkingHours.value}</Text>
										</Flex>
									)}

								{props.salaryTitle && props.salary?.value && (
									<Flex>
										<Text mb={0}>{props.salaryTitle}:&nbsp;</Text>
										<Text mb={0}>{props.salary.value}</Text>
									</Flex>
								)}

								{props.employmentAdmissionTitle &&
									props.employmentAdmission?.value && (
										<Flex>
											<Text mb={0}>
												{props.employmentAdmissionTitle}:&nbsp;
											</Text>
											<Text mb={0}>{props.employmentAdmission.value}</Text>
										</Flex>
									)}
								{/* Button link if VarbiSpontaneousApplicationCareerPage */}
								{props.applicationLink?.value && (
									<Flex>
										<Button.Anchor
											href={props.applicationLink.value}
											onClick={() =>
												sendJobAdDataLayerPush(
													"declare_interest",
													props.heading.value
												)
											}
											my={4}
										>
											{props.applicationLinkText}
										</Button.Anchor>
									</Flex>
								)}
							</Flex>

							<Flex flexDirection="column" mb={5}>
								{props.contactTitle && props.contact?.value && (
									<>
										<Text variant="headline-2" mb={3}>
											{props.contactTitle}
										</Text>
										<Text mb={2}>
											<span
												dangerouslySetInnerHTML={createMarkup(
													props.contact.value
												)}
											></span>
										</Text>
									</>
								)}
								{props.externalApplicationLink?.value && props.applyButtonText && (
									<Flex>
										<Button.Anchor
											href={props.externalApplicationLink.value}
											onClick={() => {
												sendJobAdDataLayerPush("apply", props.heading.value)
											}}
										>
											{props.applyButtonText}
										</Button.Anchor>
									</Flex>
								)}
							</Flex>

							{props.aboutCapioTitle && props.aboutCapio?.value && (
								<Flex flexDirection="column" mb={3}>
									<Text variant="headline-2" mb={3}>
										{props.aboutCapioTitle}
									</Text>

									<Rte>
										<div
											dangerouslySetInnerHTML={createMarkup(
												props.aboutCapio.value
											)}
										/>
									</Rte>
								</Flex>
							)}
						</Flex>
					</Flex>
				</Flex>
			</Flex>
		</Flex>
	)
}

export { VarbiCareerPage }
